import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Drawer, Table, Card, Radio, Input, Space, Select, DrawerProps, Modal, Button } from 'antd';
import { useTranslate } from '@tolgee/react';
import dayjs from 'lib/dayjs';
import { useMemoizedFn, usePagination } from 'ahooks';
import {
  getBotServeBuyerSummary,
  getBotAbandonDetail,
  getBotServeBuyerDetail,
  GetBotServeBuyerDetail,
  BotServeBuyerDetail,
  BotServeBuyerSummary,
  BotAbandonDetail,
} from 'services/api/aiReplyService';
import { useAuthorization } from 'components/Authorization';
import StoreSelector from 'components/StoreSelector';
import { useGetBotReplyTypeText } from 'views/Conversation/Chat/components/MessageScenario/ScenarioContext';
import { SearchOutlined } from '@ant-design/icons';
import { StoreInfo } from 'services/api/store';
import { toNumber } from 'lodash-es';

const DATE_FORMAT = 'YYYY.MM.DD HH:mm:ss';

enum TabKey {
  Buyer = 'Buyer',
  Dismiss = 'Dismiss',
}

// 搜索条件
interface SearchFilter {
  storeId?: string;
  buyerNick?: string;
  month?: number;
}

export interface AIServiceRecordsProps extends DrawerProps {}

const DEFAULT_PAGE_SIZE = 10;

interface RecordTableProps {
  searchFilter: SearchFilter;
}

const EMPTY_PAGINATION = {
  total: 0,
  list: [],
};

// AI参与服务买家记录
const BuyerRecordTable = (props: RecordTableProps) => {
  const { searchFilter } = props;
  const [detailVisible, setDetailVisible] = useState(false);
  const { t } = useTranslate();
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const getBotReplyTypeText = useGetBotReplyTypeText();
  const [detailModalTitle, setDetailModalTitle] = useState('');

  // 获取服务买家记录列表请求
  const {
    data: buyerSummaryData,
    loading: buyerSummaryLoading,
    pagination: buyerSummaryPagination,
  } = usePagination(
    ({ current, pageSize }) => {
      if (!payload?.orgId) {
        return Promise.resolve(EMPTY_PAGINATION);
      }
      return getBotServeBuyerSummary({
        pageNum: current,
        pageSize,
        orgId: payload.orgId,
        ...searchFilter,
      })
        .then((data) => {
          const { total, summaries } = data;
          return {
            total,
            list: summaries,
          };
        })
        .catch(() => {
          return EMPTY_PAGINATION;
        });
    },
    {
      refreshDeps: [searchFilter],
    },
  );

  // 买家明细请求
  const {
    run: getBotServeBuyerRecord,
    data: botServeBuyerData,
    loading: botServeBuyerLoading,
    pagination: botServeBuyerPagination,
  } = usePagination(
    ({ current, pageSize }, params: Omit<GetBotServeBuyerDetail, 'pageSize' | 'pageNum'>) => {
      return getBotServeBuyerDetail({
        pageNum: current,
        pageSize,
        ...params,
      })
        .then((data) => {
          const { total, details } = data;
          return {
            total,
            list: details,
          };
        })
        .catch(() => {
          return EMPTY_PAGINATION;
        });
    },
    {
      manual: true,
    },
  );

  const columns = useMemo(
    () => [
      {
        title: t('market.平台'),
        dataIndex: 'tunnelType',
        key: 'tunnelType',
      },
      {
        title: t('marketing.店铺名称'),
        dataIndex: 'storeName',
        key: 'storeName',
        render: (_: string, { storeName, alias }: BotServeBuyerSummary) => {
          return (
            <span>
              {storeName}
              {alias ? `(${alias})` : null}
            </span>
          );
        },
      },
      {
        title: t('marketing.买家昵称'),
        dataIndex: 'buyerNick',
        key: 'buyerNick',
      },
      {
        title: t('market.最后AI服务时间'),
        dataIndex: 'lastSendTime',
        key: 'lastSendTime',
        render: (_: any, { lastSendTime }: BotServeBuyerSummary) => {
          return dayjs(toNumber(lastSendTime)).format(DATE_FORMAT);
        },
      },
      {
        title: t('aeoverview.下发条数'),
        dataIndex: 'sentNum',
        key: 'sentNum',
      },
      {
        title: t('操作'),
        key: 'operation',
        render: (
          _: any,
          { storeId, buyerId, storeName, alias, buyerNick }: BotServeBuyerSummary,
        ) => {
          return (
            <Button
              type="link"
              onClick={() => {
                if (payload?.orgId) {
                  setDetailVisible(true);
                  setDetailModalTitle(
                    `${t('marketing.查看明细')}(${alias || storeName}-${buyerNick})`,
                  );
                  getBotServeBuyerRecord(
                    {
                      current: 1,
                      pageSize: DEFAULT_PAGE_SIZE,
                    },
                    {
                      orgId: payload.orgId,
                      storeId,
                      buyerId,
                      month: searchFilter.month,
                    },
                  );
                }
              }}
            >
              {t('marketing.查看明细')}
            </Button>
          );
        },
      },
    ],
    [t, getBotServeBuyerRecord, payload, searchFilter.month],
  );

  // 买家明细表格
  const detailColumns = useMemo(
    () => [
      {
        title: t('aeoverview.下发场景'),
        dataIndex: 'botReplyType',
        key: 'botReplyType',
        render: (_: string, { botReplyType, extra }: BotServeBuyerDetail) => {
          return getBotReplyTypeText(botReplyType, extra);
        },
      },
      {
        title: t('aeoverview.时间'),
        dataIndex: 'sendTime',
        key: 'sendTime',
        render: (_: string, { sendTime }: BotServeBuyerDetail) => {
          return dayjs(toNumber(sendTime)).format(DATE_FORMAT);
        },
      },
    ],
    [t, getBotReplyTypeText],
  );

  return (
    <>
      <Table
        rowKey={(record) => {
          const { tunnelType, storeId, buyerId, lastSendTime } = record;
          return `${tunnelType}-${storeId}-${buyerId}-${lastSendTime}`;
        }}
        dataSource={buyerSummaryData?.list}
        loading={buyerSummaryLoading}
        columns={columns}
        pagination={{
          total: buyerSummaryData?.total,
          current: buyerSummaryPagination.current,
          pageSize: buyerSummaryPagination.pageSize,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => t('feedback.共x项', { x: total }),
          onChange: buyerSummaryPagination.onChange,
          onShowSizeChange: buyerSummaryPagination.onChange,
        }}
      />
      <Modal
        title={detailModalTitle}
        visible={detailVisible}
        style={{ top: 20 }}
        footer={null}
        onCancel={() => {
          setDetailVisible(false);
        }}
      >
        <Table
          dataSource={botServeBuyerData?.list}
          loading={botServeBuyerLoading}
          columns={detailColumns}
          pagination={{
            size: 'small',
            current: botServeBuyerPagination.current,
            total: botServeBuyerData?.total,
            showTotal: (total) => t('feedback.共x项', { x: total }),
            onChange: botServeBuyerPagination.onChange,
          }}
        />
      </Modal>
    </>
  );
};

// 错失服务机会记录
const DismissRecordTable = (props: RecordTableProps) => {
  const { searchFilter } = props;
  const { t } = useTranslate();

  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const getBotReplyTypeText = useGetBotReplyTypeText();

  /**
   * 获取错误服务记录
   */
  const {
    data: botAbandonData,
    loading: botAbandonLoading,
    pagination: botAbandonPagination,
  } = usePagination(
    ({ current, pageSize }) => {
      if (!payload?.orgId) {
        return Promise.resolve(EMPTY_PAGINATION);
      }
      return getBotAbandonDetail({
        pageNum: current,
        pageSize,
        orgId: payload.orgId,
        ...searchFilter,
      })
        .then((data) => {
          const { total, details } = data;
          return {
            total,
            list: details,
          };
        })
        .catch(() => {
          return EMPTY_PAGINATION;
        });
    },
    {
      refreshDeps: [searchFilter],
    },
  );

  const columns = useMemo(
    () => [
      {
        title: t('market.平台'),
        dataIndex: 'tunnelType',
        key: 'tunnelType',
      },
      {
        title: t('marketing.店铺名称'),
        dataIndex: 'storeName',
        key: 'storeName',
        render: (_: string, { storeName, alias }: BotAbandonDetail) => {
          return (
            <span>
              {storeName}
              {alias ? `(${alias})` : null}
            </span>
          );
        },
      },
      {
        title: t('marketing.买家昵称'),
        dataIndex: 'buyerNick',
        key: 'buyerNick',
      },
      {
        title: t('aeoverview.可下发场景'),
        dataIndex: 'botReplyType',
        key: 'botReplyType',
        render: (_: string, { botReplyType, extra }: BotAbandonDetail) => {
          return getBotReplyTypeText(botReplyType, extra);
        },
      },
      {
        title: t('aeoverview.时间'),
        dataIndex: 'sendTime',
        key: 'sendTime',
        render: (_: string, { sendTime }: BotAbandonDetail) => {
          return dayjs(toNumber(sendTime)).format(DATE_FORMAT);
        },
      },
    ],
    [t, getBotReplyTypeText],
  );

  return (
    <Table
      rowKey={(record) => {
        const { tunnelType, storeId, buyerId, sendTime } = record;
        return `${tunnelType}-${storeId}-${buyerId}-${sendTime}`;
      }}
      dataSource={botAbandonData?.list}
      loading={botAbandonLoading}
      columns={columns}
      pagination={{
        total: botAbandonData?.total,
        current: botAbandonPagination.current,
        pageSize: botAbandonPagination.pageSize,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => t('feedback.共x项', { x: total }),
        onChange: botAbandonPagination.onChange,
        onShowSizeChange: botAbandonPagination.onChange,
      }}
    />
  );
};

// 当前月和上个月的数据
const mouthValues = {
  current: dayjs().utcOffset(8).startOf('month'),
  prev: dayjs().utcOffset(8).subtract(1, 'month').startOf('month'),
};

const defaultSearchFilter: SearchFilter = {
  storeId: undefined,
  buyerNick: undefined,
  month: mouthValues.current.valueOf(),
};
/**
 * 查看服务记录弹窗层
 */
const AIServiceRecords = (props: AIServiceRecordsProps) => {
  const { t } = useTranslate();
  const [activeTabKey, setActiveTabKey] = useState<TabKey>(TabKey.Buyer);

  const [searchFilter, setSearchFilter] = useState<SearchFilter>(defaultSearchFilter);
  const [storeSelected, setStoreSelected] = useState<StoreInfo | undefined>();
  const [buyerNickText, setBuyerNickText] = useState<string>('');

  const tabList = useMemo(
    () => [
      {
        key: TabKey.Buyer,
        tab: t('market.AI参与服务买家记录'),
      },
      {
        key: TabKey.Dismiss,
        tab: t('market.错失服务机会记录'),
      },
    ],
    [t],
  );

  /**
   * 月份选择: 当前月和上个月
   */
  const months = dayjs.months();
  const mouthOptions = [
    {
      text: months[mouthValues.prev.month()],
      value: mouthValues.prev.valueOf(),
    },
    {
      text: months[mouthValues.current.month()],
      value: mouthValues.current.valueOf(),
    },
  ];

  const handleBuyerNick = useMemoizedFn(() => {
    setSearchFilter((prev) => ({
      ...prev,
      buyerNick: buyerNickText,
    }));
  });

  return (
    <Container title={t('market.查看服务记录')} destroyOnClose {...props}>
      <Content direction="vertical" size={16}>
        <Space>
          {/* 筛选, 搜索 */}
          <StoreSelector
            scene="BASIC_RECEPTION"
            quickSelectDisable
            mutilple={false}
            onChange={({ storeList }) => {
              if (storeList?.length) {
                const [storeInfo] = storeList;
                setStoreSelected(storeInfo);
                setSearchFilter((prev) => ({
                  ...prev,
                  storeId: storeInfo.storeId,
                }));
              } else {
                setStoreSelected(undefined);
                setSearchFilter((prev) => ({
                  ...prev,
                  storeId: undefined,
                }));
              }
            }}
            displayNodeRender={() => {
              return (
                <Select
                  style={{ width: 160 }}
                  placeholder={t('market.选择店铺')}
                  value={storeSelected?.storeName}
                  allowClear
                  onClear={() => {
                    setStoreSelected(undefined);
                    setSearchFilter((prev) => ({
                      ...prev,
                      storeId: undefined,
                    }));
                  }}
                />
              );
            }}
          />
          <Input
            allowClear
            value={buyerNickText}
            placeholder={t('market.请输入买家昵称')}
            suffix={<SearchOutlined onClick={handleBuyerNick} />}
            onPressEnter={handleBuyerNick}
            onChange={(e) => {
              setBuyerNickText((prev) => {
                if (prev && !e.target.value) {
                  setSearchFilter((prev) => ({
                    ...prev,
                    buyerNick: undefined,
                  }));
                }
                return e.target.value.trim();
              });
            }}
          />
          <Radio.Group
            value={searchFilter.month}
            onChange={(e) => {
              setSearchFilter((prev) => ({
                ...prev,
                month: e.target.value,
              }));
            }}
          >
            {mouthOptions.map((option) => {
              return (
                <Radio.Button key={option.value} value={option.value}>
                  {option.text}
                </Radio.Button>
              );
            })}
          </Radio.Group>
        </Space>
        <Card
          tabList={tabList}
          activeTabKey={activeTabKey}
          onTabChange={(tab) => {
            setActiveTabKey(tab as TabKey);
          }}
        >
          {activeTabKey === TabKey.Buyer && <BuyerRecordTable searchFilter={searchFilter} />}
          {activeTabKey === TabKey.Dismiss && <DismissRecordTable searchFilter={searchFilter} />}
        </Card>
      </Content>
    </Container>
  );
};

export default AIServiceRecords;

const Container = styled(Drawer)`
  .ant-drawer-content-wrapper {
    width: 78% !important;
    min-width: 800px;
  }
  .ant-drawer-body {
    background-color: var(--fett-bg-app);
  }
`;

const Content = styled(Space)`
  width: 100%;
`;
